import { BrowserModule } from '@angular/platform-browser'
import { ErrorHandler, NgModule } from '@angular/core'
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt'
import { registerLocaleData } from '@angular/common';

import { StoreRouterConnectingModule, RouterStateSerializer } from '@ngrx/router-store'
import { StoreModule } from '@ngrx/store'
import { EffectsModule } from '@ngrx/effects'
import { LocalStorageModule } from 'angular-2-local-storage'
import { EmbedVideo } from 'ngx-embed-video'

import { TranslateLoader, TranslateModule } from '@ngx-translate/core'

import { NgbModule } from '@ng-bootstrap/ng-bootstrap'

import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'

import { FormComponent } from './core/base/form-component'
import { ReactiveFormComponent } from './core/base/reactive-form-component'

import { AuthGuard } from './auth/services/auth-guard-service'
import { metaReducers, reducers } from './reducers'

import { OrderHistoryModule } from './orderhistory/orderhistory.module'
import { BackordersModule } from './backorders/backorders.module'
import { OrdersModule } from './orders/orders.module'
import { CatalogModule } from './catalog/catalog.module'
import { CartModule } from './cart/cart.module'
import { CheckoutModule } from './checkout/checkout.module'
import { BaseModule } from './base/base.module'
import { AuthModule } from './auth/auth.module'
import { PagesModule } from './pages/pages.module'
import { environment } from '../environments/environment'
// Core services
import { AuthService } from './auth/services/auth.service'
import { FormsService } from './core/services/forms.service'
import { CrashService } from './core/services/crash.service'
// Import http interceptor
import { AuthInterceptor } from './core/services/auth.interceptor'
import { SourceInterceptor } from './core/services/source.interceptor'
import { TenantInterceptor } from './core/services/tenant.interceptor'
// Factory for AOT compliance
// import { HttpLoaderFactory } from './core/services/http-loader-factory'
import { TranslateCustomHttpLoader } from './core/services/translate-custom-http-loader.service'
// Error Handler
import { LanguageInterceptor } from './core/services/language.interceptor';
import { SalesRepGuard } from './auth/services/sales-rep-guard-service';
import { ModalGalleryModule } from 'angular-modal-gallery';
import 'hammerjs';
import 'mousetrap';
import { RaygunErrorHandler } from './core/services/raygun.error.handler';
import { CustomRouterStateSerializer } from './shared/utils/custom-router-state';

// Register locale data
import localeNl from '@angular/common/locales/nl';
import localeEn from '@angular/common/locales/en';
import localePl from '@angular/common/locales/pl';
import localeCh from '@angular/common/locales/de-CH';
import localeDa from '@angular/common/locales/da';
import localeDe from '@angular/common/locales/de';

registerLocaleData(localeNl, 'nl');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDa, 'da');
registerLocaleData(localePl, 'pl');
registerLocaleData(localeCh, 'ch');
registerLocaleData(localeDe, 'de');

// adding rx operators

export function jwtOptionsFactory(authSvc: AuthService) {
  return {
    tokenGetter: () => {
      return new Promise(resolve => {
        authSvc.getToken().subscribe(token => {
          if (token !== null)
            resolve(token)
          else
            resolve('empty')
        })
      })
    },
    headerName: 'Authorization',
    authScheme: 'JWT ',
    throwNoTokenError: true,
    whitelistedDomains: ['localhost:3000'],
    blacklistedRoutes: [/\/auth.*/, 'http://localhost:4200', /\/assets.*/]
  }
}

@NgModule({
  declarations: [
    AppComponent,
    FormComponent,
    ReactiveFormComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    NgbModule.forRoot(),
    BaseModule.forRoot(),
    ModalGalleryModule.forRoot(),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [AuthService]
      }
    }),
    TranslateModule.forRoot({
      loader: {
          provide: TranslateLoader,
          useClass: TranslateCustomHttpLoader,
          // useFactory: HttpLoaderFactory,
          deps: [HttpClient]
      }
    }),
    LocalStorageModule.withConfig({
      prefix: 'iconum',
      storageType: 'sessionStorage'
    }),
    EmbedVideo.forRoot(),
    CartModule.forRoot(),
    AuthModule.forRoot(),
    OrdersModule.forRoot(),
    BackordersModule.forRoot(),
    OrderHistoryModule.forRoot(),
    CatalogModule.forRoot(),
    CheckoutModule.forRoot(),
    PagesModule.forRoot(),
    AppRoutingModule,
    HttpClientModule,
    StoreModule.forRoot(
      reducers,
      {
        metaReducers
      }
    ),
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router'
    }),
    EffectsModule.forRoot([]),
  ],
  providers: [
    AuthGuard,
    SalesRepGuard,
    AuthService,
    FormsService,
    CrashService,
    {
      provide: ErrorHandler,
      useClass: RaygunErrorHandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SourceInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TenantInterceptor,
      multi: true
    },
    {
      provide: RouterStateSerializer,
      useClass: CustomRouterStateSerializer
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
